import { useState, useMemo, useEffect } from 'react';
import { motion } from 'motion/react';
import { ProductGrid } from './ProductGrid';
import { Input } from './ui/input';
import { ChevronLeft } from 'lucide-react';
import { Header } from './Header';
import Currency from '../../src/utils/Currency.js';

export function SearchView({
  currency,
  locale,
  showHeaderThemeChanger,
  showHeaderSearchView,
  products,
  cart,
  cartItems,
  total,
  sellers,
  sellerColors,
  onCancel,
  onProductClick,
  cartCount,
  onHomeClick,
  onSearchClick,
  onCartClick,
  onLogoClick,
  onCheckout,
  style,
  filteredStoreIds,
  logActivity
}) {
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const input = document.getElementById('search-input');
    if (input) input.focus();
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return [];
    }
    const query = searchQuery.toLowerCase().trim();
    const results = products.filter(product => 
      (product && product.label       && product.label      .toLowerCase().includes(query)) ||
      (product && product.name        && product.name       .toLowerCase().includes(query)) ||
      (product && product.id          && product.id         .toLowerCase().includes(query)) ||
      (product && product.description && product.description.toLowerCase().includes(query))
    );
    let featuredProducts = results.filter(product => product.is_featured);
    featuredProducts = featuredProducts.sort((p1, p2) => {
      const p1Order = p1.order || Number.MAX_SAFE_INTEGER;
      const p2Order = p2.order || Number.MAX_SAFE_INTEGER;
      return p1Order - p2Order;
    });
    let normalProducts = results.filter(product => !product.is_featured);
    normalProducts = normalProducts.sort((p1, p2) => {
      const p1Name = p1.name || '';
      const p2Name = p2.name || '';
      return p1Name.localeCompare(p2Name);
    });
    return featuredProducts.concat(normalProducts);
  }, [searchQuery, products]);

  const handleChange = (value) => {
    logActivity('search-view', 'search-input', 'change', true, {query: value || null});
    setSearchQuery(value);
  };

  const handleCancel = () => {
    logActivity('search-view', 'back-button', 'click', true, {query: searchQuery || null});
    onCancel();
  };

  // Igual que el botón Pagar del catálogo: lleva al carro, no al modal de pago.
  const handleCheckout = () => {
    logActivity('search-view', 'checkout-button', 'click', true, {cart_details: cart.details});
    onCheckout();
  };

  const hasQuery = searchQuery.trim().length > 0;

  return (
    <div className="relative w-full min-h-(--app-h) bg-mid overflow-y-auto">
      <Header
        showThemeChanger={showHeaderThemeChanger}
        showSearchView={showHeaderSearchView}
        cartCount={cartCount}
        onHomeClick={onHomeClick}
        onSearchClick={onSearchClick}
        onCartClick={onCartClick}
        onLogoClick={onLogoClick}
        logActivity={logActivity}
        style={style}
      />
      <div className="max-w-screen-xl mx-auto">
        <div className="bg-mid box-border content-stretch flex flex-col items-start w-full px-5 pt-15 pb-5">
          <div className="content-stretch flex h-[36px] items-start relative shrink-0 w-full mb-[16px]">
            <p className="basis-0 secondary-font-bold font-semibold grow lh-36 min-h-px min-w-px not-italic relative shrink-0 fs-30 text-center tracking-[0.3955px]">
              Buscar o escanear producto
            </p>
          </div>
        </div>
        <div className="w-full px-12">
          <Input
            id="search-input"
            type="text"
            placeholder="Escribe aquí para buscar productos"
            value={searchQuery}
            onChange={(e) => handleChange(e.target.value)}
            className="h-[64px] w-full rounded-[16px] border-3 border-[#d1d5dc] px-[24px] main-font-bold text-2xl placeholder:text-[#99a1af] tracking-[-0.4395px] focus:border-[#DE2D7A] focus:ring-0"
          />
        </div>
        {hasQuery && (
          <div className="w-full pb-[168px] mt-10">
            {searchResults.length > 0 ? (
              <>
                <p className="secondary-font-bold fs-20 text-center">
                  {searchResults.length} {searchResults.length === 1 ? 'resultado' : 'resultados'} encontrado{searchResults.length === 1 ? '' : 's'}
                </p>
                <ProductGrid
                  currency={currency}
                  locale={locale}
                  from="search"
                  query={searchQuery}
                  products={searchResults}
                  sellers={sellers}
                  sellerColors={sellerColors}
                  style={style}
                  onProductClick={onProductClick}
                  filteredStoreIds={filteredStoreIds}
                  logActivity={logActivity}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-2">
                <p className="secondary-font-bold fs-24 mb-[12px]">
                  No se encontraron productos
                </p>
                <p className="secondary-font-regular fs-18">
                  Intenta con otro término de búsqueda
                </p>
              </div>
            )}
          </div>
        )}
        <div className="bg-mid fixed bottom-0 left-0 right-0 shadow-[0px_-1px_3px_0px_rgba(0,0,0,0.1),0px_-1px_2px_-1px_rgba(0,0,0,0.1)] px-8 py-6 gap-4 z-20">
          <div className='max-w-screen-xl mx-auto flex justify-between items-center gap-4'>
            <motion.button
              onClick={handleCancel}
              className="cursor-pointer btn btn-blank gap-2 w-[200px] h-[120px] border-none secondary-font-bold"
              whileTap={{ scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
            >
              <ChevronLeft className="w-8 h-8 text-bold" />
              <span className="text-bold fs-28">Volver</span>
            </motion.button>

            {cartItems.length === 0 ? (
              <motion.div
                onClick={null}
                disabled={true}
                className="opacity-50 w-[650px] h-[120px] rounded-[40px] btn btn-primary flex items-center justify-center shadow-lg"
              >
                <span className="text-5xl lh-38">Pagar ({new Currency(currency, locale).format(total)})</span>
              </motion.div>
            ) : null}

            {cartItems.length > 0 ? (
              <motion.button
                onClick={handleCheckout}
                disabled={cartItems.length === 0}
                className="cursor-pointer w-[650px] h-[120px] rounded-[40px] btn btn-primary flex items-center justify-center shadow-lg disabled:opacity-50"
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.03 }}
                animate={cartItems.length === 0 ? {} : { boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)'] }}
                transition={cartItems.length === 0 ? {} : { boxShadow: { delay: 0, duration: 2, repeat: Infinity, ease: 'linear' } }}
              >
                <span className="text-5xl lh-38">Pagar ({new Currency(currency, locale).format(total)})</span>
              </motion.button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
